import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { UseFormSetError } from 'react-hook-form';
import { useUserQuery } from '../hooks/useUserQuery';
import { useUpdateUserMutation } from '../hooks/useUpdateUserMutation';
import { UserForm } from '../components/UserForm';
import { handleUserMutationError } from '../services/userErrorHandler';
import { updateUserSchema } from '../schemas/user.schema';
import type { UpdateUserFormInputs } from '../schemas/user.schema';
import { Alert, TextLink } from '@/shared/components';

export function UserEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userQuery = useUserQuery(id ?? '');
  const mutation = useUpdateUserMutation();
  const [error, setError] = useState<string | null>(null);
  const formSetErrorRef = useRef<UseFormSetError<UpdateUserFormInputs> | null>(null);

  if (!id) {
    return <p className="text-mp-error">Invalid user ID.</p>;
  }

  if (userQuery.isLoading) {
    return <p>Loading user...</p>;
  }

  if (userQuery.isError || !userQuery.data) {
    return <p className="text-mp-error">Failed to load user.</p>;
  }

  const user = userQuery.data;

  const handleSubmit = (data: UpdateUserFormInputs) => {
    setError(null);
    mutation.mutate(
      { id, payload: { name: data.name, email: data.email } },
      {
        onSuccess: () => {
          navigate(`/admin/users/${id}`);
        },
        onError: (err) => {
          const result = handleUserMutationError(err);
          if (result.status === 422 && result.fieldErrors && formSetErrorRef.current) {
            for (const [field, messages] of Object.entries(result.fieldErrors)) {
              formSetErrorRef.current(
                field as Parameters<UseFormSetError<UpdateUserFormInputs>>[0],
                { type: 'server', message: messages[0] ?? 'Invalid' },
              );
            }
            return;
          }
          if (!result.handled) {
            setError(err.message || 'Failed to update user');
          }
        },
      },
    );
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <TextLink to={`/admin/users/${id}`} color="secondary" className="text-sm">
          &larr; Back to User
        </TextLink>
      </div>

      <h1 className="text-2xl font-semibold">Edit User</h1>

      {error && (
        <Alert variant="error">
          <span>{error}</span>
        </Alert>
      )}

      <UserForm
        defaultValues={{
          name: user.name,
          email: user.email != user.email_lower ? '' : user.email, // Don't pre-fill email if it has uppercase letters to avoid confusion with case-insensitive uniqueness validation
        }}
        onSubmit={handleSubmit}
        isLoading={mutation.isPending}
        submitLabel="Save Changes"
        schema={updateUserSchema}
        hideExtraFields={true}
        onSetErrorRef={(s) => { formSetErrorRef.current = s; }}
      />
    </section>
  );
}
